/**
 * The demand itself: who asked, what arrived, what is wanted, and where it stands in triage.
 *
 * A QUOTE REQUEST IS A SPECIES WITH A SATELLITE, since 2026-10-27. The record -- number, name,
 * audit trail, attachments, form, project membership -- is an Object like its four siblings'; the
 * request's own fields stayed on raw.QUOTE_REQUEST because the triage queue filters and computes
 * with them. The standard panes show the record. This one shows the request, and without it the
 * conversion would have carried the subject as a name and the body as a description and dropped
 * everything else that was asked -- the fact-recorded-and-not-carried-to-the-reader shape.
 *
 * KEYED ON THE LOGICAL OBJECT, not the revision, and not the satellite's own USID. The record
 * page and a project membership both hold the object; the list service takes it and answers the
 * one row behind it, finished or not, which is what makes this pane and the member pane the same
 * component with the same read.
 *
 * READ-ONLY, DELIBERATELY. Triage -- attaching a customer or an item, declining, closing -- is a
 * write with its own service and its own rules, and the first version of a pane is for seeing. A
 * request also has no lifecycle buttons of its own: the folder is what gets released.
 */
import React, {useContext, useEffect, useState} from "react";
import {Box, CircularProgress, Typography} from "@mui/material";

import {AppContext} from "../../util/AppContext";
import {makeCresAxios} from "../../util/Axios";
import {Grey7, Grey8, Grey17} from "../../theme/styles";

type Request = {
    USID: string;
    ObjectUSID?: string | null; ObjectID?: string | null; RequestNumber?: string | null;
    TriageStatus?: string | null; Status?: string | null;
    RequesterName?: string | null; RequesterEmail?: string | null;
    CustomerObjectUSID?: string | null; ItemObjectUSID?: string | null;
    Subject?: string | null; Body?: string | null;
    RequestedProduct?: string | null; Quantity?: number | string | null;
    UnitOfMeasure?: string | null;
    DetectedProductType?: string | null; ExtractedProductName?: string | null;
    Confidence?: number | string | null;
    Notes?: string | null; ReceivedDatetime?: string | null;
};

type Props = {
    /** The LOGICAL object the request is. Null while the record is still loading. */
    objectUSID?: string | null;
};

function when(value?: string | null): string {
    if (!value) return "—";
    // THE DATABASE WRITES UTC AND SAYS NOTHING ABOUT IT: "2026-10-27T22:12:04" with no offset,
    // which a browser reads as local and shows seven hours late on the west coast. Saying Z is
    // the whole fix. (Other panes in this estate make the same mistake; this one does not.)
    const text = String(value).replace(" ", "T");
    const at = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(text) ? text : `${text}Z`);
    if (Number.isNaN(at.getTime())) return String(value).slice(0, 10);
    return at.toLocaleString(undefined, {day: "numeric", month: "short", year: "numeric",
                                         hour: "2-digit", minute: "2-digit"});
}

/** The three triage states a reader distinguishes: open, answered, finished. */
const TRIAGE_WORDS: Record<string, string> = {
    RECEIVED: "Received — waiting to be quoted",
    QUOTED: "Quoted — an offer has been drafted against it",
    DECLINED: "Declined — we will not quote it",
    WITHDRAWN: "Withdrawn — the customer took it back",
    CLOSED: "Closed — dealt with, no offer",
};

function Field({label, value, wide}: {label: string; value: React.ReactNode; wide?: boolean}) {
    return (
        <Box sx={{minWidth: wide ? "100%" : 220, flex: wide ? "1 1 100%" : "0 1 auto"}}>
            <Typography sx={{fontSize: 11, color: Grey7, textTransform: "uppercase",
                             letterSpacing: "0.04em"}}>{label}</Typography>
            <Typography sx={{fontSize: 13, color: Grey17, whiteSpace: "pre-wrap",
                             lineHeight: 1.5}}>
                {value == null || value === "" ? "—" : value}
            </Typography>
        </Box>
    );
}

export default function QuoteRequestPane({objectUSID}: Props) {
    const appContext: any = useContext(AppContext);
    const api = makeCresAxios(appContext?.sessionContext);

    const [request, setRequest] = useState<Request | null>(null);
    const [state, setState] = useState<"loading" | "ready" | "none" | "failed">("loading");

    useEffect(() => {
        if (!objectUSID) return;
        let live = true;
        setState("loading");
        // THE LIST, NOT THE GET: Get Quote Request takes the satellite's own USID, which nothing
        // on this page holds. The list's ObjectUSID filter exists for exactly this caller and
        // returns a finished request too.
        api.get("t1/v1/quote_requests", {params: {ObjectUSID: objectUSID},
                                         headers: {"idoc-response": "v1.QuoteRequest-001"}})
            .then((answer: any) => {
                if (!live) return;
                const held = answer?.data?.PlatformMessage?.ListOfQuoteRequest?.QuoteRequest;
                const rows: Request[] = Array.isArray(held) ? held : (held ? [held] : []);
                setRequest(rows[0] ?? null);
                setState(rows[0] ? "ready" : "none");
            })
            .catch(() => { if (live) setState("failed"); });
        return () => { live = false; };
        // `api` is rebuilt every render; keying on it would refetch on every pass.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [objectUSID]);

    if (state === "loading") {
        return <Box sx={{padding: "16px 0"}}><CircularProgress size={18}/></Box>;
    }
    if (state === "failed") {
        return <Typography sx={{fontSize: 13, color: Grey7, paddingTop: "8px"}}>
            The request could not be read.
        </Typography>;
    }
    if (state === "none" || !request) {
        // A RECORD WITH NO ROW BEHIND IT. Possible for a request created through the object
        // door alone -- an integrator posting v1.QuoteRequestRevision-001 directly -- which is a
        // record that says nothing about what was asked. Said plainly rather than drawn blank.
        return <Typography sx={{fontSize: 13, color: Grey7, paddingTop: "8px"}}>
            This record carries no request: nothing was recorded about who asked or what for.
        </Typography>;
    }

    const triage = String(request.TriageStatus ?? request.Status ?? "").toUpperCase();
    const quantity = request.Quantity == null || request.Quantity === "" ? null
        : `${request.Quantity}${request.UnitOfMeasure ? ` ${request.UnitOfMeasure}` : ""}`;
    const classified = request.DetectedProductType || request.ExtractedProductName
        || request.Confidence != null;

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "18px", paddingTop: "8px",
                  maxWidth: 920}}>
            <Typography sx={{fontSize: 13, color: Grey8}}>
                {TRIAGE_WORDS[triage] ?? (triage || "—")}
            </Typography>

            <Box sx={{display: "flex", flexWrap: "wrap", gap: "16px 32px"}}>
                <Field label="Request number" value={request.ObjectID ?? request.RequestNumber}/>
                <Field label="Received" value={when(request.ReceivedDatetime)}/>
                <Field label="Requester" value={request.RequesterName}/>
                <Field label="Their email" value={request.RequesterEmail}/>
            </Box>

            <Box sx={{display: "flex", flexWrap: "wrap", gap: "16px 32px"}}>
                <Field label="Requested product" value={request.RequestedProduct}/>
                <Field label="Quantity" value={quantity}/>
            </Box>

            <Field wide label="Subject" value={request.Subject}/>
            <Field wide label="Message, as it arrived" value={request.Body}/>
            <Field wide label="Notes" value={request.Notes}/>

            {/* A READING, NOT A FACT. These three come from whoever classified the enquiry -- a
                person, a mail gateway, an integrator's model -- and ItemObjectUSID is null until
                somebody confirms. Shown only when somebody recorded them, and labelled as what
                they are, so a confidence does not read as a decision. */}
            {classified && (
                <Box sx={{display: "flex", flexWrap: "wrap", gap: "16px 32px"}}>
                    <Field label="Detected product type" value={request.DetectedProductType}/>
                    <Field label="Extracted product name" value={request.ExtractedProductName}/>
                    <Field label="Classifier confidence"
                           value={request.Confidence == null ? null
                               : `${Math.round(Number(request.Confidence) * 100)}% — a reading, not a decision`}/>
                </Box>
            )}
        </Box>
    );
}
